import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarList,
  ComplianceMeter,
  VolumeChart,
  formatDuration,
  humanise,
} from '../components/charts';
import { HelpHint } from '../components/HelpHint';
import { PriorityBadge, StatusBadge } from '../components/StatusBadge';
import { api } from '../lib/api';
import { ScrollX } from '../components/ScrollX';

/**
 * The dashboard.
 *
 * Ordered by what someone does about it, not by what is easiest to compute.
 * The tiles answer "is anything on fire"; the attention table is the list of
 * tickets to actually go and look at; the charts underneath are for the weekly
 * question of whether things are getting better or worse.
 *
 * The window control sits above everything and changes every figure at once,
 * so a number and a chart are never describing different periods.
 */
/** Cents to a readable figure. Whole shillings — the page is scanned. */
const kes = (cents: number) =>
  new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    maximumFractionDigits: 0,
  }).format(cents / 100);

export function DashboardPage() {
  const [days, setDays] = useState(30);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dashboard', days],
    queryFn: () => api.dashboard(days),
    refetchInterval: 60_000,
  });

  if (isPending) return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  if (isError) {
    return <p className="mt-16 text-center text-danger-600">{(error as Error).message}</p>;
  }

  const h = data.headline;

  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Dashboard</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Live counts. Trends and averages over the last {data.windowDays} days.
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDays(option)}
              className={`rounded-lg border px-3 py-1.5 text-sm ${
                days === option
                  ? 'border-primary-500 bg-primary-50 text-primary-800'
                  : 'border-border text-muted-foreground hover:border-muted-foreground'
              }`}
            >
              {option} days
            </button>
          ))}
        </div>
      </div>

      <SectionLabel tone="danger">Right now</SectionLabel>

      {/* ── Is anything on fire ─────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Open tickets" value={h.open} to="/list" />
        <Tile
          label="SLA breached"
          value={h.breached}
          tone={h.breached > 0 ? 'critical' : 'calm'}
          hint="Past their resolution target and still open."
        />
        <Tile
          label="Due soon"
          value={h.atRisk}
          tone={h.atRisk > 0 ? 'warning' : 'calm'}
          hint="More than three quarters of the target used, but not breached yet. These are the ones still worth saving."
        />
        <Tile
          label="Waiting for a technician"
          value={h.unassigned}
          tone={h.unassigned > 0 ? 'warning' : 'calm'}
          to="/"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Technicians on shift" value={`${h.techniciansOnShift}/${h.techniciansTotal}`} to="/technicians" />
        <Tile
          label="Median time to resolve"
          value={formatDuration(h.medianResolutionMs)}
          hint="Business hours only — an overnight fault is not charged for the night."
        />
        <Tile
          label="Slowest tenth"
          value={formatDuration(h.p90ResolutionMs)}
          hint="Nine in ten finished faster than this. The tail is what customers complain about, and the median hides it."
        />
        <Tile
          label="Satisfaction"
          value={h.satisfaction === null ? '—' : `${h.satisfaction}/5`}
          hint={
            h.ratingCount
              ? `From ${h.ratingCount} rating${h.ratingCount === 1 ? '' : 's'} in this period.`
              : 'Nobody has rated a visit in this period.'
          }
        />
      </div>

      {/* ── The rest of the business ──────────────────────────────────── */}
      <section>
        <h2 className="flex items-center gap-1.5 font-heading text-lg">
          The rest of the business
          <HelpHint>
            One figure each for the things that are not tickets. Every one of them has something
            to do behind it — which is the test a dashboard number has to pass. Totals that only
            ever go up are deliberately not here: they say nothing about today and they crowd out
            the ones that do.
          </HelpHint>
        </h2>

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="Owed by customers"
            value={kes(data.business.owedCents)}
            tone={data.business.invoicesOver90Days > 0 ? 'critical' : 'calm'}
            to="/arrears"
            hint={
              `Across ${data.business.owedBy} customer${data.business.owedBy === 1 ? '' : 's'}. ` +
              'Money owed is not money lost — how old it is decides which of the two it is, and ' +
              'the arrears page breaks it down by age.'
            }
          />
          <Tile
            label="Invoices unpaid"
            value={data.business.invoicesUnpaid}
            tone={data.business.invoicesOverdue > 0 ? 'warning' : 'calm'}
            to="/invoices"
            hint={
              `${data.business.invoicesOverdue} past their due date, ` +
              `${data.business.invoicesOver90Days} more than ninety days old. ` +
              'An invoice over ninety days is the one worth a telephone call today.'
            }
          />
          <Tile
            label="Stock running low"
            value={`${data.business.stockLow}/${data.business.stockWatched}`}
            tone={
              data.business.stockOut > 0
                ? 'critical'
                : data.business.stockLow > 0
                  ? 'warning'
                  : 'calm'
            }
            to="/stock"
            hint={
              `${data.business.stockOut} have none left at all. ` +
              'Counted against the reorder levels you set — an item with no reorder level is not ' +
              'counted here, because nothing can say whether its quantity is right.'
            }
          />
          <Tile
            label="Meters due for power"
            value={`${data.business.metersDue}/${data.business.metersActive}`}
            tone={data.business.metersDue > 0 ? 'warning' : 'calm'}
            to="/power"
            hint={
              'Decided by the same planner the Power screen uses, so the two figures agree. ' +
              'A meter never topped up at all counts as due — it is the one most easily missed.'
            }
          />
        </div>
      </section>

      {/* ── Questions worth asking ────────────────────────────────────── */}
      <section className="card">
        <h3 className="flex items-center gap-1.5 font-heading text-lg">
          Questions worth asking
          <HelpHint>
            The questions people actually ask of this system, each one click away with the answer
            already counted. They are shortcuts into the ordinary screens rather than reports of
            their own — so what you land on can be sorted, filtered, exported and worked like any
            other list.
          </HelpHint>
        </h3>

        <ul className="mt-2 divide-y divide-border">
          {[
            {
              to: '/list',
              question: 'Which tickets have breached their SLA?',
              answer: `${h.breached} now, ${h.atRisk} close to it`,
              urgent: h.breached > 0,
            },
            {
              to: '/list',
              question: 'What has nobody been assigned to?',
              answer: `${h.unassigned} waiting for somebody`,
              urgent: h.unassigned > 0,
            },
            {
              to: '/arrears',
              question: 'Who owes us money, oldest first?',
              answer:
                data.business.invoicesOver90Days > 0
                  ? `${data.business.invoicesOver90Days} invoices over ninety days old`
                  : `${data.business.owedBy} customers owe something`,
              urgent: data.business.invoicesOver90Days > 0,
            },
            {
              to: '/stock/list',
              question: 'What is about to run out?',
              answer:
                data.business.stockOut > 0
                  ? `${data.business.stockOut} items have none left`
                  : `${data.business.stockLow} below their reorder level`,
              urgent: data.business.stockOut > 0,
            },
            {
              to: '/power',
              question: 'Which meters need topping up?',
              answer: `${data.business.metersDue} due now`,
              urgent: data.business.metersDue > 0,
            },
            {
              to: '/contracts',
              question: 'What is waiting on a signature?',
              answer: `${data.business.awaitingSignature} contracts`,
              urgent: data.business.awaitingSignature > 0,
            },
            {
              to: '/list',
              question: 'What finished without a photograph or a materials list?',
              answer: `${data.revenueRisk.noPhotos + data.revenueRisk.noMaterials} jobs that cannot yet be billed`,
              urgent: data.revenueRisk.noPhotos + data.revenueRisk.noMaterials > 0,
            },
          ].map((row) => (
            <li key={row.question}>
              <Link
                to={row.to}
                className="flex flex-wrap items-center justify-between gap-2 py-2.5 hover:bg-muted/40"
              >
                <span className="font-medium">{row.question}</span>
                <span
                  className={`text-sm ${
                    row.urgent ? 'font-medium text-accent-800' : 'text-muted-foreground'
                  }`}
                >
                  {row.answer}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* ── What to go and look at ──────────────────────────────────────── */}
      <section className="card">
        <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
          Needs attention now
          <HelpHint>
            Breached first, then closest to breaching. This is the list to work through, not a
            summary — every row links to the ticket.
          </HelpHint>
        </h3>
        {data.attention.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nothing is breached or close to it.
          </p>
        ) : (
          <ScrollX className="-mx-4 sm:mx-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold">Ticket</th>
                  <th className="px-4 py-2 font-semibold">State</th>
                  <th className="px-4 py-2 font-semibold">SLA used</th>
                  <th className="px-4 py-2 font-semibold">Area</th>
                  <th className="px-4 py-2 font-semibold">Technician</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.attention.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2">
                      <Link to={`/tickets/${row.id}`} className="text-primary-600 hover:underline">
                        {row.reference}
                      </Link>
                      <span className="ml-2 text-muted-foreground">{row.title}</span>
                      <div className="mt-1 flex gap-1.5">
                        <StatusBadge status={row.status} />
                        <PriorityBadge priority={row.priority} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      {/* The word, not just the colour — this pair is the one a
                          colourblind reader cannot separate by hue. */}
                      <span
                        className={`badge ${
                          row.state === 'breached'
                            ? 'bg-danger-50 text-danger-700'
                            : 'bg-accent-50 text-accent-700'
                        }`}
                      >
                        {row.state === 'breached' ? 'Breached' : 'Due soon'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 tabular-nums">
                      {row.targetMin ? `${Math.round(row.usedFraction * 100)}%` : '—'}
                      <span className="ml-1 text-xs text-muted-foreground">
                        {formatDuration(row.elapsedMs)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                      {row.area ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                      {row.assignee ?? 'Nobody yet'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        )}
      </section>

      <SectionLabel tone="warning">Money at risk</SectionLabel>

      {/*
        Work already paid for in wages, fuel and equipment that cannot yet be
        billed or defended. Each of these is a job somebody did: the cost has
        been incurred either way, and the only question is whether there is a
        record good enough to charge for it and to stand behind if a customer
        argues. On the dashboard rather than in a report, because every one is
        fixable the same day it appears.
      */}
      <section className="card">
        <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
          Where money leaks
          <HelpHint>
            Every row is work that has already cost you. A finished job with no photo cannot be
            defended if the customer disputes it; one with no items recorded cannot be reconciled
            against stock. Both are money out with nothing to show.
          </HelpHint>
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          These should all be zero. Anything above zero is a job to chase today.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="Finished, no photo"
            value={data.revenueRisk.noPhotos}
            tone={data.revenueRisk.noPhotos > 0 ? 'critical' : 'calm'}
            hint="Work that cannot be proved was done. The first thing a disputing customer asks for."
          />
          <Tile
            label="Finished, no items recorded"
            value={data.revenueRisk.noMaterials}
            tone={data.revenueRisk.noMaterials > 0 ? 'critical' : 'calm'}
            hint="Equipment left site with no record. It cannot be billed, reconciled or traced."
          />
          <Tile
            label="Fixed, no root cause"
            value={data.revenueRisk.noRootCause}
            tone={data.revenueRisk.noRootCause > 0 ? 'warning' : 'calm'}
            hint="A repeat fault nobody can spot, because the first visit never said what was wrong."
          />
          <Tile
            label="Open, nobody assigned"
            value={data.revenueRisk.unassignedOpen}
            tone={data.revenueRisk.unassignedOpen > 0 ? 'warning' : 'calm'}
            to="/"
            hint="An SLA clock running with nobody working on it."
          />
        </div>
      </section>

      <SectionLabel tone="info">Trends</SectionLabel>

      {/* ── Is it getting better ────────────────────────────────────────── */}
      <section className="card">
        <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
          Work in and out
          <HelpHint>
            If the created line sits above resolved for several days, the backlog is growing
            whatever the totals say.
          </HelpHint>
        </h3>
        <VolumeChart data={data.volume} />
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card">
          <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
            Resolved within target
            <HelpHint>
              Of the tickets closed in this period, the share that met their SLA. The mark on the
              track is the 90% target.
            </HelpHint>
          </h3>
          {h.slaCompliance === null ? (
            <p className="text-sm text-muted-foreground">Nothing closed in this period yet.</p>
          ) : (
            <>
              <ComplianceMeter percent={h.slaCompliance} />
              <p className="mt-3 text-sm text-muted-foreground">
                {h.closedInWindow} ticket{h.closedInWindow === 1 ? '' : 's'} closed in the last{' '}
                {data.windowDays} days.
              </p>
            </>
          )}
        </section>

        <section className="card">
          <h3 className="mb-3 font-heading text-lg">Open by stage</h3>
          <BarList data={data.byStatus.map((s) => ({ ...s, label: humanise(s.label) }))} />
        </section>

        <section className="card">
          <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
            Open by area
            <HelpHint>
              Only the areas GoSpeedNet serves. A name here that surprises you is a ticket logged
              against the wrong place.
            </HelpHint>
          </h3>
          <BarList data={data.byArea} />
        </section>

        {/*
          What the business consumed. Read next to the wasted-trip figures on
          the Technicians page: fibre that went out on a job that found no
          fault is fibre that was paid for twice.
        */}
        <section className="card">
          <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
            Stock used
            <HelpHint>
              Recorded by technicians when they finish a job. Cable is in metres because that is how
              it is bought and billed.
            </HelpHint>
          </h3>
          {data.stock.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing recorded in the last {data.windowDays} days.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {data.stock.map((line) => (
                <li key={line.code} className="flex items-center justify-between py-2">
                  <span>{line.name}</span>
                  <span className="font-medium tabular-nums">
                    {line.quantity}
                    <span className="ml-1 text-xs text-muted-foreground">
                      {line.unit === 'metre' ? 'm' : 'pcs'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
            Current workload
            <HelpHint>
              Open jobs per technician right now. Someone at zero while others are stacked usually
              means their zones or skills need a look.
            </HelpHint>
          </h3>
          <BarList
            data={data.workload.map((t) => ({
              label: t.isActive ? t.name : `${t.name} (off shift)`,
              count: t.open,
            }))}
            limit={10}
          />
        </section>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone = 'calm',
  to,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'calm' | 'warning' | 'critical';
  to?: string;
}) {
  const body = (
    <div
      className={`card h-full ${
        tone === 'critical'
          ? 'border-danger-200 bg-danger-50/40'
          : tone === 'warning'
            ? 'border-accent-200 bg-accent-50/40'
            : ''
      }`}
    >
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {label}
        {hint && <HelpHint>{hint}</HelpHint>}
      </div>
      <div
        className={`mt-1 text-3xl font-semibold tabular-nums ${
          tone === 'critical' ? 'text-danger-700' : tone === 'warning' ? 'text-accent-700' : ''
        }`}
      >
        {value}
      </div>
    </div>
  );

  return to ? (
    <Link to={to} className="block transition hover:opacity-90">
      {body}
    </Link>
  ) : (
    body
  );
}

/**
 * A coloured band that names the section under it.
 *
 * The dashboard is long enough that somebody scrolling needs to know what part
 * of the business they are looking at without reading every heading. The three
 * tones map to how urgent the section is, not to decoration: red is what is
 * happening now, amber is what is quietly costing money, blue is history.
 */
function SectionLabel({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'danger' | 'warning' | 'info';
}) {
  const style =
    tone === 'danger'
      ? 'bg-danger-500'
      : tone === 'warning'
        ? 'bg-accent-500'
        : 'bg-primary-500';

  return (
    <div className="flex items-center gap-3 pt-2">
      <span
        className={`inline-block rounded-md px-2.5 py-1 text-xs font-semibold label-mono tracking-[0.12em] text-white ${style}`}
      >
        {children}
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}
